// App 外殼，DOM 結構與 class 逐一對齊舊版 crm/src/App.jsx 的 Root（樣式必須一模一樣）。
import { useEffect, useRef, useState } from 'react'
import { Outlet, useBlocker, useLocation } from 'react-router'
import { account, keepalive } from '@/lib/auth'
import { isKnownPath } from './routes'
import { findBlockingGuard } from './leaveGuard'
import { SideMenu } from './SideMenu'
import { DrawerMenu } from './DrawerMenu'
import { useRealtime } from './useRealtime'
import { LanguageSwitch } from './LanguageSwitch'
import { Account } from './Account'
import { t } from '@/i18n'
import { CloudDownloadIcon } from '@/components/ui/icons'
import { ExportCenterDialog } from '@/features/export-center/components/ExportCenterDialog'
import { useHasNewExport } from '@/features/export-center/exportCenter'
import { LAST_VALID_PATH_KEY } from './RouteFallback'
import Logo from '@/assets/logo-choice.svg'

// 內層限高捲動的頁面（區塊定高、清單在內部捲），其餘頁面整頁捲動
const FIXED_HEIGHT_PATHS = ['/home', '/dashboard', '/message-inbox', '/account-permissions']

export function AppLayout() {
  const { pathname, search } = useLocation()
  const [drawerOn, setDrawerOn] = useState(false)
  const [centerOpen, setCenterOpen] = useState(false)
  const hasNewExport = useHasNewExport()
  const mainRef = useRef(null)
  const pageRef = useRef(null)
  const isFixedHeightPage = FIXED_HEIGHT_PATHS.includes(pathname) || pathname.startsWith('/dashboard/')

  useRealtime()

  useEffect(() => {
    const timer = setTimeout(keepalive, 60_000)
    return () => clearTimeout(timer)
  }, [])

  // 同舊版：只有 push 導航才捲回頂端（含 useUrlState 預設的篩選更新，舊版 Solid 也是 push）；replace 不捲
  useEffect(() => {
    const original = history.pushState
    history.pushState = function (...args) {
      original.apply(this, args)
      mainRef.current?.scrollTo(0, 0)
    }
    return () => {
      history.pushState = original
    }
  }, [])

  // 換頁重播過場動畫。不用 key={pathname} 重掛：/lines/:pid 各 tab 是同一個元件，重掛會丟狀態
  useEffect(() => {
    const el = pageRef.current
    if (!el) return
    el.classList.remove('page-transition')
    void el.offsetWidth
    el.classList.add('page-transition')
  }, [pathname])

  // 記錄最後一次成功匹配的路由（含查詢參數），亂打網址整頁重載後 RouteFallback 據此導回
  useEffect(() => {
    if (isKnownPath(pathname)) sessionStorage.setItem(LAST_VALID_PATH_KEY, pathname + search)
  }, [pathname, search])

  // 全站唯一的 useBlocker（react-router 同時只認一個），兩種情況會擋：
  //   1. 站內導航到未知路由：靜默取消，留在原頁；上一頁／下一頁（POP）放行（同舊版 useBeforeLeave）
  //   2. 頁面用 useLeaveGuard 登記的守衛（例如未儲存離開確認）：交給該守衛的 onBlocked 處理
  const blockedBy = useRef(null)
  const blocker = useBlocker((args) => {
    if (args.historyAction !== 'POP' && !isKnownPath(args.nextLocation.pathname)) {
      blockedBy.current = 'unknown-path'
      return true
    }
    blockedBy.current = findBlockingGuard(args)
    return Boolean(blockedBy.current)
  })
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    const reason = blockedBy.current
    blockedBy.current = null
    if (reason && reason !== 'unknown-path') reason.onBlocked(blocker)
    else blocker.reset()
  }, [blocker])

  return (
    <div className="drawer">
      <input id="side-drawer-menu" type="checkbox" onChange={(e) => setDrawerOn(e.target.checked)} checked={drawerOn} className="drawer-toggle" />
      <div className="drawer-content app-shell-bg fixed inset-0 flex flex-col">
        <header className="flex-none">
          <div className="flex h-[52px] items-center gap-1 px-3.25 md:px-5">
            <div className="flex flex-1 items-center gap-2">
              <div className="flex-none md:hidden">
                <label htmlFor="side-drawer-menu" className="btn btn-square btn-ghost swap swap-rotate drawer-button text-brand-black-800">
                  <input type="checkbox" />
                  <svg className="swap-off fill-current" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512"><path d="M64,384H448V341.33H64Zm0-106.67H448V234.67H64ZM64,128v42.67H448V128Z" /></svg>
                  <svg className="swap-on fill-current" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512"><polygon points="400 145.49 366.51 112 256 222.51 145.49 112 112 145.49 222.51 256 112 366.51 145.49 400 256 289.49 366.51 400 400 366.51 289.49 256 400 145.49" /></svg>
                </label>
              </div>
              <img src={Logo} className="h-9 w-auto" />
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {account.env && <span className="hidden text-xs leading-[18px] text-brand-black-500 sm:inline">Environment：{account.env}</span>}
              <button type="button" className="navbar-icon-btn relative" title={t('app', 'Download Center')} aria-label={t('app', 'Download Center')} onClick={() => setCenterOpen(true)}>
                <CloudDownloadIcon className="size-4" />
                {hasNewExport && <span className="absolute top-1 right-1 size-3 rounded-full border-2 border-white bg-brand-primary-light-5" />}
              </button>
              <LanguageSwitch />
              <Account />
            </div>
          </div>
        </header>
        <div className="flex flex-1 overflow-hidden">
          <SideMenu />
          <div className="flex min-w-0 flex-1 flex-col pt-1">
            <main className="relative flex-1 overflow-hidden rounded-t-[20px] md:rounded-tr-none md:rounded-tl-[12px] bg-base-100">
              <div ref={mainRef} className="absolute inset-0 overflow-y-auto scrollbar-stable px-4 pt-4 md:px-6 md:pt-6">
                <div ref={pageRef} className={`page-transition flex flex-col ${isFixedHeightPage ? 'h-[calc(100%-1rem)]' : 'min-h-[calc(100%-1rem)]'}`}>
                  <Outlet />
                </div>
                <div className="h-4"></div>
              </div>
            </main>
          </div>
        </div>
        {/* Toast 掛在 App.jsx */}
        <ExportCenterDialog open={centerOpen} onClose={() => setCenterOpen(false)} />
      </div>

      <div className="drawer-side">
        <label htmlFor="side-drawer-menu" aria-label="close sidebar" className="drawer-overlay"></label>
        <DrawerMenu onNavigate={() => setDrawerOn(false)} />
      </div>
    </div>
  )
}
